import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/layout/Layout';
import { useLanguage } from '../contexts/LanguageContext';
import { getAccountingEntries, getAccountingSummary, getAccountingCategories, createAccountingEntry, deleteAccountingEntry } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Plus,
  TrendUp,
  TrendDown,
  Scales,
  Trash,
  ChartLine,
  ArrowUp,
  ArrowDown,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export function AccountingPage() {
  const { t } = useLanguage();
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [categories, setCategories] = useState({ income: [], expenses: [] });
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    entry_type: 'ingreso',
    category: '',
    description: '',
    amount: 0,
    date: new Date().toISOString().split('T')[0],
    notes: '',
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, [typeFilter, selectedMonth]);

  const fetchData = async () => {
    try {
      const [year, month] = selectedMonth.split('-');
      const params = { date_from: `${selectedMonth}-01`, date_to: `${selectedMonth}-31` };
      if (typeFilter !== 'all') params.entry_type = typeFilter;
      const [entriesRes, summaryRes, categoriesRes] = await Promise.all([
        getAccountingEntries(params), getAccountingSummary({ month, year }), getAccountingCategories(),
      ]);
      setEntries(entriesRes.data);
      setSummary(summaryRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      toast.error(t('errorOccurred'));
    } finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.category) { toast.error(t('selectCategory')); return; }
    try {
      await createAccountingEntry(formData);
      toast.success(t('entryCreated'));
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || t('errorOccurred')); }
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('deleteRecord'))) {
      try {
        await deleteAccountingEntry(id);
        toast.success(t('entryDeleted'));
        fetchData();
      } catch (error) { toast.error(t('errorOccurred')); }
    }
  };

  const resetForm = () => {
    setFormData({ entry_type: 'ingreso', category: '', description: '', amount: 0, date: new Date().toISOString().split('T')[0], notes: '' });
  };

  const getCategoryLabel = (category) => {
    const allCategories = [...categories.income, ...categories.expenses, ...(categories.system || [])];
    const found = allCategories.find(c => c.value === category);
    return found?.label || category;
  };

  const currentCategories = formData.entry_type === 'ingreso' ? categories.income : categories.expenses;

  return (
    <Layout title={t('accounting')}>
      <div className="space-y-6" data-testid="accounting-page">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="valhalla-card p-5 bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 border-emerald-500/30">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-[#0B0E14]/50 text-emerald-400"><ArrowUp weight="bold" className="w-5 h-5" /></div>
              <div>
                <p className="text-xs text-[#9CA3AF] uppercase tracking-wider">{t('income')}</p>
                <p className="font-['Outfit'] text-2xl font-bold text-emerald-400">${summary?.income?.total?.toLocaleString() || 0}</p>
              </div>
            </div>
          </div>
          <div className="valhalla-card p-5 bg-gradient-to-br from-red-500/20 to-red-500/5 border-red-500/30">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-[#0B0E14]/50 text-red-400"><ArrowDown weight="bold" className="w-5 h-5" /></div>
              <div>
                <p className="text-xs text-[#9CA3AF] uppercase tracking-wider">{t('expenses')}</p>
                <p className="font-['Outfit'] text-2xl font-bold text-red-400">${summary?.expenses?.total?.toLocaleString() || 0}</p>
              </div>
            </div>
          </div>
          <div className={`valhalla-card p-5 bg-gradient-to-br ${summary?.net_profit >= 0 ? 'from-[#E2B05E]/20 to-[#E2B05E]/5 border-[#E2B05E]/30' : 'from-red-500/20 to-red-500/5 border-red-500/30'}`}>
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-lg bg-[#0B0E14]/50 ${summary?.net_profit >= 0 ? 'text-[#E2B05E]' : 'text-red-400'}`}><Scales weight="duotone" className="w-5 h-5" /></div>
              <div>
                <p className="text-xs text-[#9CA3AF] uppercase tracking-wider">{t('netBalance')}</p>
                <p className={`font-['Outfit'] text-2xl font-bold ${summary?.net_profit >= 0 ? 'text-[#E2B05E]' : 'text-red-400'}`}>${summary?.net_profit?.toLocaleString() || 0}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="valhalla-card p-5">
            <h3 className="font-['Outfit'] text-lg font-semibold text-[#F3F4F6] mb-4 flex items-center gap-2">
              <TrendUp className="w-5 h-5 text-emerald-400" />{t('incomeByCategory')}
            </h3>
            <div className="space-y-3">
              {summary?.income?.by_category && Object.entries(summary.income.by_category).map(([cat, amount]) => (
                <div key={cat} className="flex items-center justify-between p-3 rounded-lg bg-[#0B0E14]/50">
                  <span className="text-[#F3F4F6] capitalize">{getCategoryLabel(cat)}</span>
                  <span className="font-['Outfit'] font-bold text-emerald-400">${amount.toLocaleString()}</span>
                </div>
              ))}
              {(!summary?.income?.by_category || Object.keys(summary.income.by_category).length === 0) && (
                <p className="text-[#9CA3AF] text-center py-4">{t('noIncomeThisMonth')}</p>
              )}
            </div>
          </div>
          <div className="valhalla-card p-5">
            <h3 className="font-['Outfit'] text-lg font-semibold text-[#F3F4F6] mb-4 flex items-center gap-2">
              <TrendDown className="w-5 h-5 text-red-400" />{t('expensesByCategory')}
            </h3>
            <div className="space-y-3">
              {summary?.expenses?.by_category && Object.entries(summary.expenses.by_category).map(([cat, amount]) => (
                <div key={cat} className="flex items-center justify-between p-3 rounded-lg bg-[#0B0E14]/50">
                  <span className="text-[#F3F4F6] capitalize">{getCategoryLabel(cat)}</span>
                  <span className="font-['Outfit'] font-bold text-red-400">${amount.toLocaleString()}</span>
                </div>
              ))}
              {(!summary?.expenses?.by_category || Object.keys(summary.expenses.by_category).length === 0) && (
                <p className="text-[#9CA3AF] text-center py-4">{t('noExpensesThisMonth')}</p>
              )}
            </div>
          </div>
        </div>

        {/* Filters + New Entry */}
        <div className="flex flex-col sm:flex-row gap-4 justify-between">
          <div className="flex items-center gap-3">
            <Input type="month" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} className="valhalla-input w-44" />
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-36 valhalla-input"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#151B26] border-[#242E42]">
                <SelectItem value="all">{t('all')}</SelectItem>
                <SelectItem value="ingreso">{t('income')}</SelectItem>
                <SelectItem value="gasto">{t('expenses')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#E2B05E] hover:bg-[#F0C47A] text-[#0B0E14] font-semibold" data-testid="new-entry-btn">
                <Plus className="w-4 h-4 mr-2" />{t('newEntry')}
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#151B26] border-[#242E42] max-w-md">
              <DialogHeader><DialogTitle className="text-[#F3F4F6] font-['Outfit']">{t('newAccountingEntry')}</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <Tabs value={formData.entry_type} onValueChange={(v) => setFormData({...formData, entry_type: v, category: ''})}>
                  <TabsList className="w-full bg-[#0B0E14] border border-[#242E42]">
                    <TabsTrigger value="ingreso" className="flex-1 data-[state=active]:bg-emerald-500 data-[state=active]:text-white"><ArrowUp className="w-4 h-4 mr-2" />{t('incomeTab')}</TabsTrigger>
                    <TabsTrigger value="gasto" className="flex-1 data-[state=active]:bg-red-500 data-[state=active]:text-white"><ArrowDown className="w-4 h-4 mr-2" />{t('expenseTab')}</TabsTrigger>
                  </TabsList>
                </Tabs>
                <div className="space-y-2">
                  <Label className="text-[#9CA3AF]">{t('category')}</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                    <SelectTrigger className="valhalla-input"><SelectValue placeholder={t('selectCategory')} /></SelectTrigger>
                    <SelectContent className="bg-[#151B26] border-[#242E42]">
                      {currentCategories.map(cat => (<SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-[#9CA3AF]">
                    {formData.entry_type === 'ingreso'
                      ? 'Los cobros de facturas se registran automaticamente al marcar una factura como pagada.'
                      : 'Los pagos a proveedores se registran automaticamente desde ordenes de compra y pagos a proveedores.'}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#9CA3AF]">{t('description')}</Label>
                  <Input value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="valhalla-input" required />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#9CA3AF]">{t('amount')}</Label>
                    <Input type="number" step="0.01" value={formData.amount} onChange={(e) => setFormData({...formData, amount: parseFloat(e.target.value) || 0})} className="valhalla-input" required />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#9CA3AF]">{t('date')}</Label>
                    <Input type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} className="valhalla-input" required />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#9CA3AF]">{t('notesOptional')}</Label>
                  <Textarea value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} className="valhalla-input" />
                </div>
                <div className="flex justify-end gap-3 pt-4 border-t border-[#242E42]">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="border-[#242E42] text-[#9CA3AF]">{t('cancel')}</Button>
                  <Button type="submit" className={formData.entry_type === 'ingreso' ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-red-500 hover:bg-red-600'}>{t('save')}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Entries List */}
        <div className="valhalla-card overflow-hidden">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('date')}</th>
                <th>{t('status')}</th>
                <th>{t('category')}</th>
                <th>{t('description')}</th>
                <th>{t('amount')}</th>
                <th>{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry._id}>
                  <td className="text-[#9CA3AF]">{entry.date}</td>
                  <td>
                    <Badge className={entry.entry_type === 'ingreso' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}>
                      {entry.entry_type === 'ingreso' ? t('incomeTab') : t('expenseTab')}
                    </Badge>
                  </td>
                  <td className="text-[#F3F4F6] capitalize">
                    <div className="flex flex-wrap items-center gap-2">
                      <span>{getCategoryLabel(entry.category)}</span>
                      {entry.auto_generated && (
                        <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                          Auto
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className="text-[#F3F4F6]">{entry.description}</td>
                  <td className={`font-['Outfit'] font-bold ${entry.entry_type === 'ingreso' ? 'text-emerald-400' : 'text-red-400'}`}>
                    {entry.entry_type === 'ingreso' ? '+' : '-'}${entry.amount?.toFixed(2)}
                  </td>
                  <td>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(entry._id)}
                      disabled={entry.auto_generated}
                      title={entry.auto_generated ? 'Se corrige desde el modulo que lo genero' : undefined}
                      className="text-[#9CA3AF] hover:text-red-400 disabled:opacity-40 disabled:hover:text-[#9CA3AF]"
                    >
                      <Trash className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {entries.length === 0 && !loading && (
          <div className="valhalla-card p-12 text-center">
            <ChartLine className="w-12 h-12 mx-auto text-[#9CA3AF] mb-4" />
            <p className="text-[#9CA3AF]">{t('noRecordsPeriod')}</p>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default AccountingPage;
